import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { FiSearch, FiChevronLeft, FiChevronRight, FiChevronDown, FiPlus, FiChevronUp, FiMoreVertical, FiFolder, FiX, FiEdit, FiEye, FiDownload, FiTrash2 } from 'react-icons/fi'
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import MetricCard from '@/components/ui/MetricCard'
import UploadContractFileModal from './UploadContractFileModal'
import AddSupplierModal from './AddSupplierModal'
import ContractDetailsModal from './ContractDetailsModal'
import EditContractModal from './EditContractModal'
import ImagePopupModal from '@/components/ui/ImagePopupModal'
import { ROUTES } from '@/constants/routes'
import { formatDateToDDMMMYYYY } from '@/utils/dateFormat'
import { formatSkuDisplay } from '@/utils/skuFormat'
import { formatShippingPeriod } from '@/utils/shippingPeriod'
import { formatContractPrice } from '@/constants/contractCurrencies'
import { useContracts } from '@/hooks/useContracts'
import { useContractPerformance } from '@/hooks/useContractPerformance'
import * as contractsApi from '@/services/api/contracts'
import { getVendors } from '@/services/api/vendors'
import { vendorsForContractPicker } from '@/utils/vendorFilters'
import PageSizeSelect from '@/components/common/PageSizeSelect'
import type { Contract } from '@/services/api/contracts'
import type { Vendor } from '@/services/api/vendors'

interface ContractData {
  id: string
  name: string
  ref?: string
  type: string
  supplier: string
  contractType: string
  product: string
  sku?: string
  qty: string
  price: string
  priceCurrency?: string
  deliveryConditions?: string
  deliveryPeriod?: string
  terms?: string
  buyContract?: string
  saleContract?: string
  dateModified: string
  isFolder?: boolean
  customer?: string
  parentId?: string
  fileUrl?: string | null
  incoterm?: string
  factory?: string
  forwarder?: string
  pol?: string
  pod?: string
  booking?: string
  paymentTerms?: string
}

type TabKey = 'buy-contract' | 'sale-contract' | 'contract-performance'

function isImageUrl(url: string): boolean {
  try {
    const path = new URL(url).pathname.toLowerCase()
    return /\.(jpg|jpeg|png|gif|webp|bmp)(\?|$)/i.test(path)
  } catch {
    return false
  }
}

const ContractFile = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  // Initialize activeTab based on current route
  const getInitialTab = (): TabKey => {
    if (location.pathname === ROUTES.CONTRACTS_SALE) return 'sale-contract'
    if (location.pathname === ROUTES.CONTRACTS_PERFORMANCE) return 'contract-performance'
    if (location.pathname === ROUTES.CONTRACTS_BUY) return 'buy-contract'
    // Default to buy-contract for /contracts or other contract routes
    return 'buy-contract'
  }

  const getInitialExpandedFolders = (): Set<string> => {
    const initialTab = getInitialTab()
    if (initialTab === 'buy-contract') {
      return new Set(['buy-folder-1'])
    } else if (initialTab === 'sale-contract') {
      return new Set(['folder-1'])
    }
    return new Set()
  }

  const [activeTab, setActiveTab] = useState<TabKey>(getInitialTab())
  const [currentPage, setCurrentPage] = useState(1)
  const [resultsPerPage, setResultsPerPage] = useState(10)
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') ?? '')
  const [supplierFilter, setSupplierFilter] = useState('Select Supplier')
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(getInitialExpandedFolders())
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  const [performanceYear, setPerformanceYear] = useState(new Date().getFullYear())
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isAddSupplierModalOpen, setIsAddSupplierModalOpen] = useState(false)
  const [isContractDetailsModalOpen, setIsContractDetailsModalOpen] = useState(false)
  const [selectedContract, setSelectedContract] = useState<ContractData | null>(null)
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [actionError, setActionError] = useState<string | null>(null)
  const [uploadingContractId, setUploadingContractId] = useState<string | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editContractId, setEditContractId] = useState<string | null>(null)
  const [fileViewerUrl, setFileViewerUrl] = useState<string | null>(null)
  const [fileViewerPan, setFileViewerPan] = useState({ x: 0, y: 0 })
  const [fileViewerDrag, setFileViewerDrag] = useState<{ startX: number; startY: number; startPanX: number; startPanY: number } | null>(null)
  const [imagePopupSrc, setImagePopupSrc] = useState<string | null>(null)
  const rowUploadInputRef = useRef<HTMLInputElement>(null)

  // Reset pan when opening file viewer; handle drag-to-pan
  useEffect(() => {
    if (!fileViewerUrl) {
      setFileViewerPan({ x: 0, y: 0 })
      setFileViewerDrag(null)
      return
    }
    const onMouseMove = (e: MouseEvent) => {
      if (!fileViewerDrag) return
      setFileViewerPan({
        x: fileViewerDrag.startPanX + (e.clientX - fileViewerDrag.startX),
        y: fileViewerDrag.startPanY + (e.clientY - fileViewerDrag.startY)
      })
    }
    const onMouseUp = () => setFileViewerDrag(null)
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [fileViewerUrl, fileViewerDrag])
  const uploadTargetContractIdRef = useRef<string | null>(null)

  // Map API Contract to table row (ContractData)
  const mapContractToRow = useCallback((c: Contract): ContractData => ({
    id: c.id,
    name: c.title,
    ref: c.contractNumber,
    type: 'Contract',
    supplier: c.vendor?.name ?? 'No Supplier',
    contractType: c.type === 'buy' ? 'Buy Contract' : 'Sale contract',
    product: c.productSpecifications ?? '-',
    sku: c.sku ? formatSkuDisplay(c.sku) : '-',
    qty: c.quantity ?? '-',
    price: c.price ?? '-',
    priceCurrency: c.priceCurrency ?? undefined,
    deliveryConditions: c.deliveryConditions ?? '-',
    deliveryPeriod: formatShippingPeriod(c.startDate, c.endDate, c.deliveryPeriod),
    terms: c.deliveryConditions ?? '-',
    buyContract: c.fileUrl ? 'Uploaded' : 'Upload',
    saleContract: c.fileUrl ? 'Uploaded' : 'Upload',
    dateModified: c.updatedAt,
    isFolder: false,
    customer: c.customer ?? c.vendor?.name ?? '-',
    fileUrl: c.fileUrl,
    incoterm: c.incoterm ?? '-',
    factory: c.factory ?? '-',
    forwarder: c.forwarder ?? '-',
    pol: c.pol ?? '-',
    pod: c.pod ?? '-',
    booking: c.booking ?? '-',
    paymentTerms: c.paymentTerms ?? '-',
  }), [])

  // Fetch contracts from API when on Buy or Sale tab
  const contractTypeForApi = activeTab === 'buy-contract' ? 'buy' : activeTab === 'sale-contract' ? 'sale' : null
  const { contracts, meta, loading: contractsLoading, error: contractsError, refetch } = useContracts({
    type: contractTypeForApi,
    search: searchQuery,
    supplier: supplierFilter === 'Select Supplier' ? '' : supplierFilter,
    page: currentPage,
    limit: resultsPerPage,
    enabled: activeTab === 'buy-contract' || activeTab === 'sale-contract',
  })

  // Fetch contract performance data when on Performance tab
  const { data: performanceData, loading: performanceLoading, error: performanceError } = useContractPerformance({
    year: performanceYear,
    enabled: activeTab === 'contract-performance',
  })

  // Flat list of table rows from API (no folders)
  const allContractData: ContractData[] = useMemo(
    () => (activeTab === 'buy-contract' || activeTab === 'sale-contract' ? contracts.map(mapContractToRow) : []),
    [activeTab, contracts, mapContractToRow]
  )

  // Fetch vendors for supplier dropdown when on Buy or Sale tab
  useEffect(() => {
    if (activeTab === 'buy-contract' || activeTab === 'sale-contract') {
      getVendors({ limit: 200 })
        .then((res) => setVendors(vendorsForContractPicker(res.vendors)))
        .catch(() => setVendors([]))
    }
  }, [activeTab])

  // Update active tab when route changes
  useEffect(() => {
    const newTab = getInitialTab()
    setActiveTab(newTab)
    if (newTab === 'buy-contract') setExpandedFolders(new Set(['buy-folder-1']))
    else if (newTab === 'sale-contract') setExpandedFolders(new Set(['folder-1']))
    else setExpandedFolders(new Set())
  }, [location.pathname])

  const tabs: { label: string; key: TabKey }[] = [
    { label: 'Buy Contract', key: 'buy-contract' },
    { label: 'Sale Contract', key: 'sale-contract' },
    { label: 'Contract Performance', key: 'contract-performance' },
  ]

  // Get search placeholder based on active tab
  const getSearchPlaceholder = (tab: TabKey): string => {
    switch (tab) {
      case 'sale-contract':
        return 'Search sale contract files...'
      case 'buy-contract':
        return 'Search buy contract files...'
      case 'contract-performance':
        return 'Search contract performance files...'
      default:
        return 'Search contract files...'
    }
  }

  // Supplier options for dropdown (from vendors API)
  const suppliersForFilter = useMemo(() => {
    return vendors.map((v) => ({ id: v.id, name: v.name }))
  }, [vendors])

  // For Buy/Sale tabs we use API data directly (already filtered by type, search, supplier). No client-side folders.
  const filteredData = useMemo(() => allContractData, [allContractData])

  const handleTabClick = (tabKey: TabKey) => {
    setActiveTab(tabKey)
    setCurrentPage(1)
    setSearchQuery('') // Reset search when switching tabs
    setSupplierFilter('Select Supplier')
    
    // Reset expanded folders based on new tab
    if (tabKey === 'buy-contract') {
      setExpandedFolders(new Set(['buy-folder-1']))
    } else if (tabKey === 'sale-contract') {
      setExpandedFolders(new Set(['folder-1']))
    } else {
      setExpandedFolders(new Set())
    }
    
    // Navigate to the appropriate route
    switch (tabKey) {
      case 'buy-contract':
        navigate(ROUTES.CONTRACTS_BUY)
        break
      case 'sale-contract':
        navigate(ROUTES.CONTRACTS_SALE)
        break
      case 'contract-performance':
        navigate(ROUTES.CONTRACTS_PERFORMANCE)
        break
    }
  }

  const toggleFolder = (folderId: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev)
      if (newSet.has(folderId)) {
        newSet.delete(folderId)
      } else {
        newSet.add(folderId)
      }
      return newSet
    })
  }

  const toggleDropdown = (itemId: string) => {
    setOpenDropdownId(prev => prev === itemId ? null : itemId)
  }

  const handleViewContract = (contract: ContractData) => {
    setSelectedContract(contract)
    setIsContractDetailsModalOpen(true)
    setOpenDropdownId(null)
  }

  const handleRowClick = (contract: ContractData) => {
    if (!contract.isFolder && (activeTab === 'sale-contract' || activeTab === 'buy-contract')) {
      handleViewContract(contract)
    }
  }

  const handleDeleteContract = async (item: ContractData) => {
    if (!window.confirm(`Delete contract "${item.name}"?`)) return
    setActionError(null)
    try {
      await contractsApi.deleteContract(item.id)
      refetch()
      setOpenDropdownId(null)
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete contract')
    }
  }

  const handleDownloadContract = async (item: ContractData) => {
    if (!item.fileUrl) {
      setActionError('No file attached to this contract')
      setOpenDropdownId(null)
      return
    }
    setActionError(null)
    try {
      const viewUrl = await contractsApi.getContractFileViewUrl(item.id)
      if (isImageUrl(viewUrl)) {
        setImagePopupSrc(viewUrl)
      } else {
        setFileViewerUrl(viewUrl)
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to open file')
    }
    setOpenDropdownId(null)
  }

  const handleDeleteContractFile = async (contractId: string) => {
    if (!window.confirm('Remove the uploaded file from this contract?')) return
    setActionError(null)
    try {
      await contractsApi.updateContract(contractId, { fileUrl: null })
      refetch()
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove file')
    }
  }

  const handleRowUploadClick = (contractId: string) => {
    uploadTargetContractIdRef.current = contractId
    rowUploadInputRef.current?.click()
  }

  const handleContractFileButtonClick = (item: ContractData, uploaded: boolean) => {
    if (uploaded && item.fileUrl) {
      void handleDownloadContract(item)
      return
    }
    handleRowUploadClick(item.id)
  }

  const handleRowUploadFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    const contractId = uploadTargetContractIdRef.current
    e.target.value = ''
    uploadTargetContractIdRef.current = null
    if (!file || !contractId) return
    setActionError(null)
    setUploadingContractId(contractId)
    try {
      await contractsApi.uploadContractFile(contractId, file)
      refetch()
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to upload file')
    } finally {
      setUploadingContractId(null)
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openDropdownId) {
        setOpenDropdownId(null)
      }
    }
    if (openDropdownId) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [openDropdownId])

  const totalResults = meta?.total ?? 0
  const totalPages = Math.max(1, meta?.totalPages ?? 1)
  const currentData = filteredData
  const startIndex = totalResults === 0 ? 0 : (currentPage - 1) * resultsPerPage + 1
  const endIndex = currentData.length === 0 ? 0 : startIndex + currentData.length - 1

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, supplierFilter, activeTab])

  useEffect(() => {
    const q = searchParams.get('search')
    if (q !== null) setSearchQuery(q)
  }, [searchParams])

  // Contract Performance Data - use API data or fallback to empty arrays
  const contractCompletionData = performanceData?.contractCompletion || [
    { name: 'Completed Contract', value: 0, percentage: 0, color: '#212126' },
    { name: 'Active Contract', value: 0, percentage: 0, color: '#6B806B' },
  ]

  const contractedData = performanceData?.contractedData || []

  const monthlyPerformanceData = performanceData?.monthlyPerformanceData || []

  // Helper to format trend display
  const formatTrend = (value: number): string => {
    const arrow = value >= 0 ? '↑' : '↓'
    return `${arrow}${Math.abs(value)}% from last month`
  }

  // Get available years for dropdown (current year and 4 previous years)
  const currentYear = new Date().getFullYear()
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i)

  // Render Contract Performance Dashboard
  if (activeTab === 'contract-performance') {
    return (
      <div className="space-y-6" style={{ maxWidth: '100%', width: '100%', overflowX: 'hidden', boxSizing: 'border-box', padding: 0, margin: 0 }}>
        {/* Header Section */}
        <div>
          {/* First Row: Title and Breadcrumbs */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h1
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: '20px',
                lineHeight: '32px',
                letterSpacing: '0%',
                color: '#212126',
              }}
            >
              Contract File
            </h1>

            {/* Breadcrumbs */}
            <span
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '12px',
                lineHeight: '20px',
                letterSpacing: '0%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <span style={{ color: '#69697A' }}>Home</span>
              <span style={{ color: '#69697A', margin: '0 4px' }}> &gt; </span>
              <span style={{ color: '#212126' }}>Contract File</span>
            </span>
          </div>

          {/* Second Row: Tabs and Year Filter */}
          <div className="flex items-center justify-between mt-6">
            <div
              className="flex items-center"
              style={{
                width: '100%',
                maxWidth: '550px',
                height: '38px',
                borderRadius: '8px',
                border: '1px solid #E9E9EA',
                backgroundColor: '#FFFFFF',
                padding: '4px',
                gap: '0px'
              }}
            >
              {tabs.map((tab) => {
                const isActive = activeTab === tab.key

                return (
                  <button
                    key={tab.key}
                    onClick={() => handleTabClick(tab.key)}
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '14px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      color: isActive ? '#FFFFFF' : '#212126',
                      backgroundColor: isActive ? '#212126' : 'transparent',
                      borderRadius: '6px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      flex: 1,
                      justifyContent: 'center',
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    {tab.label}
                  </button>
                )
              })}
            </div>
            {/* Year Filter for Performance tab */}
            <div className="relative ml-4">
              <img
                src="/images/calendar.svg"
                alt="Calendar"
                className="absolute left-3 pointer-events-none"
                style={{ width: '18px', height: '18px', top: '50%', transform: 'translateY(-50%)' }}
              />
              <select
                value={performanceYear}
                onChange={(e) => setPerformanceYear(Number(e.target.value))}
                className="pl-10 pr-8 py-1 border border-gray-300 rounded text-sm appearance-none"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0%',
                  color: '#212126',
                  height: '38px',
                  minWidth: '120px',
                }}
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <FiChevronDown
                className="absolute right-3 pointer-events-none"
                style={{ width: '16px', height: '16px', top: '50%', transform: 'translateY(-50%)', color: '#212126' }}
              />
            </div>
          </div>
        </div>

        {/* Loading State */}
        {performanceLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
              <p style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', color: '#69697A' }}>
                Loading performance data...
              </p>
            </div>
          </div>
        )}

        {/* Error State */}
        {performanceError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', color: '#E93323' }}>
              {performanceError}
            </p>
          </div>
        )}

        {/* Performance Content */}
        {!performanceLoading && !performanceError && (
          <>
            {/* Metric Cards */}
            <div 
              className="grid grid-cols-1 sm:grid-cols-3 gap-3"
              style={{
                width: '100%',
                maxWidth: '100%',
              }}
            >
              <MetricCard
                title="Total Contract"
                value={String(performanceData?.summary?.total || 0)}
                trend={formatTrend(performanceData?.summary?.totalTrend || 0)}
                icon={<img src="/images/document-text.svg" alt="Total Contract" style={{ width: '16px', height: '16px', display: 'block', objectFit: 'contain' }} />}
                iconBgColor=""
                iconBgColorCustom="#E8F7EF"
                style={{ width: '100%', maxWidth: '100%' }}
              />
              <MetricCard
                title="Completed Contract"
                value={String(performanceData?.summary?.completed || 0)}
                trend={formatTrend(performanceData?.summary?.completedTrend || 0)}
                icon={<img src="/images/receipt-text.svg" alt="Completed Contract" style={{ width: '16px', height: '16px', display: 'block', objectFit: 'contain' }} />}
                iconBgColor=""
                iconBgColorCustom="#EAF1FF"
                style={{ width: '100%', maxWidth: '100%' }}
              />
              <MetricCard
                title="Active Contract"
                value={String((performanceData?.summary?.inProgress || 0) + (performanceData?.summary?.pending || 0))}
                trend={formatTrend(performanceData?.summary?.inProgressTrend || 0)}
                icon={<img src="/images/pending-document.svg" alt="Active Contract" style={{ width: '16px', height: '16px', display: 'block', objectFit: 'contain' }} />}
                iconBgColor=""
                iconBgColorCustom="#FEF9EB"
                style={{ width: '100%', maxWidth: '100%' }}
              />
            </div>

            {/* Charts Section */}
            <div className="flex flex-col lg:flex-row gap-4" style={{ width: '100%', maxWidth: '100%' }}>
              {/* Contract Completion Donut Chart */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 w-full lg:max-w-[400px] lg:flex-shrink-0">
                <h3
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: 'clamp(13px, 3vw, 14px)',
                    lineHeight: '28px',
                    letterSpacing: '0%',
                    color: '#212126',
                    marginBottom: '16px',
                  }}
                >
                  Contract Completion
                </h3>
                <div className="w-full" style={{ height: 'clamp(200px, 35vw, 250px)' }}>
                  <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={contractCompletionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {contractCompletionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 space-y-2">
                  {contractCompletionData.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <span
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 400,
                            fontStyle: 'normal',
                            fontSize: '12px',
                            lineHeight: '16px',
                            letterSpacing: '0%',
                            color: '#69697A',
                          }}
                        >
                          {item.name}
                        </span>
                      </div>
                      <span
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 500,
                          fontSize: '12px',
                          color: '#212126',
                        }}
                      >
                        {item.value} ({item.percentage || 0}%)
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contracted Bar Chart */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 flex-1 w-full" style={{ minWidth: 0 }}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-2 mb-4">
                  <h3
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 600,
                      fontStyle: 'normal',
                      fontSize: 'clamp(13px, 3vw, 14px)',
                      lineHeight: '28px',
                      letterSpacing: '0%',
                      color: '#212126',
                    }}
                  >
                    Contracted vs Delivered ({performanceYear})
                  </h3>
                  <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                    <button
                      className="flex items-center justify-center gap-1 hover:opacity-90 transition-opacity"
                      style={{
                        height: '34px',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        backgroundColor: '#212126',
                      }}
                    >
                      <FiDownload className="w-4 h-4" style={{ color: '#FFFFFF' }} />
                      <span
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 600,
                          fontStyle: 'normal',
                          fontSize: '12px',
                          lineHeight: '20px',
                          letterSpacing: '0%',
                          color: '#FFFFFF',
                        }}
                      >
                        Export
                      </span>
                    </button>
                  </div>
                </div>
                {contractedData.length > 0 ? (
                  <div className="w-full" style={{ height: 'clamp(250px, 40vw, 300px)' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={contractedData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={true} vertical={false} />
                      <XAxis
                        dataKey="month"
                        tick={{
                          fill: '#050521',
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontSize: '14px',
                        }}
                        axisLine={{
                          stroke: '#E9E9EA',
                          strokeWidth: 1,
                        }}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{
                          fill: '#69697A',
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontSize: '12px',
                        }}
                        tickFormatter={(value) => value === 0 ? '0' : `${value / 1000}K`}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip 
                        formatter={(value: number, name: string) => {
                          const label = name === 'totalContract' ? 'Contract Value' : 'Delivered Value'
                          return [`$${value.toLocaleString()}`, label]
                        }}
                      />
                      <Legend
                        wrapperStyle={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: '16px',
                          color: '#69697A',
                        }}
                        iconSize={8}
                        formatter={(value) => (
                          <span style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '16px',
                            color: '#69697A',
                          }}>
                            {value === 'totalContract' ? 'Contract Value' : 'Delivered Value'}
                          </span>
                        )}
                      />
                      <Bar dataKey="totalContract" fill="#2C71F6" radius={[4, 4, 0, 0]} name="totalContract" />
                      <Bar dataKey="delivered" fill="#E93323" radius={[4, 4, 0, 0]} name="delivered" />
                    </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex items-center justify-center" style={{ height: 'clamp(250px, 40vw, 300px)' }}>
                    <p style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', color: '#69697A' }}>
                      No data available for {performanceYear}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Monthly Performance Area Chart */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 w-full" style={{ boxSizing: 'border-box' }}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-2 mb-4">
                <div>
                  <h3
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 600,
                      fontStyle: 'normal',
                      fontSize: 'clamp(13px, 3vw, 14px)',
                      lineHeight: '28px',
                      letterSpacing: '0%',
                      color: '#212126',
                    }}
                  >
                    Monthly Performance ({performanceYear})
                  </h3>
                  {performanceData?.totals && (
                    <div className="flex items-center gap-4 mt-1">
                      <span style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: '#13AB5B' }}>
                        Total Profit: ${performanceData.totals.totalProfit.toLocaleString()}
                      </span>
                      <span style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: '#E93323' }}>
                        Total Costs: ${performanceData.totals.totalLoss.toLocaleString()}
                      </span>
                      <span style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: performanceData.totals.netProfit >= 0 ? '#13AB5B' : '#E93323', fontWeight: 500 }}>
                        Net: ${performanceData.totals.netProfit.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {monthlyPerformanceData.length > 0 ? (
                <div className="w-full" style={{ height: 'clamp(250px, 40vw, 300px)' }}>
                  <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={monthlyPerformanceData}
                    margin={{ left: 0, right: 20, top: 10, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="colorLoss" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#E93323" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#E93323" stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#13AB5B" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#13AB5B" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#E5E7EB"
                      horizontal={true}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="month"
                      tick={{
                        fill: '#050521',
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '14px',
                      }}
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                      axisLine={{
                        stroke: '#E9E9EA',
                        strokeWidth: 1,
                      }}
                      tickLine={false}
                      padding={{ left: 10, right: 10 }}
                      interval={0}
                    />
                    <YAxis
                      tick={{
                        fill: '#69697A',
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '12px',
                      }}
                      tickFormatter={(value) => value === 0 ? '0' : `$${(value / 1000).toFixed(0)}K`}
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '16px',
                      }}
                      axisLine={false}
                      tickLine={false}
                      domain={[0, 'auto']}
                      allowDecimals={false}
                      width={50}
                    />
                    <Tooltip 
                      formatter={(value: number, name: string) => {
                        const label = name === 'profit' ? 'Profit (Revenue)' : 'Costs'
                        return [`$${value.toLocaleString()}`, label]
                      }}
                    />
                    <Legend
                      wrapperStyle={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '16px',
                        color: '#69697A',
                      }}
                      iconSize={8}
                      formatter={(value) => (
                        <span style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: '16px',
                          color: '#69697A',
                        }}>
                          {value === 'profit' ? 'Revenue' : 'Costs'}
                        </span>
                      )}
                    />
                    <Area type="monotone" dataKey="profit" stroke="#13AB5B" fillOpacity={1} fill="url(#colorProfit)" name="profit" />
                    <Area type="monotone" dataKey="loss" stroke="#E93323" fillOpacity={1} fill="url(#colorLoss)" name="loss" />
                  </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center" style={{ height: 'clamp(250px, 40vw, 300px)' }}>
                  <p style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', color: '#69697A' }}>
                    No performance data available for {performanceYear}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6" style={{ maxWidth: '100%' }}>
      {/* Header Section */}
      <div>
        {/* First Row: Title and Breadcrumbs */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '20px',
              lineHeight: '32px',
              letterSpacing: '0%',
              color: '#212126',
            }}
          >
            Contract File
          </h1>

          {/* Breadcrumbs */}
          <span
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: '12px',
              lineHeight: '20px',
              letterSpacing: '0%',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ color: '#69697A', margin: '0 4px' }}> &gt; </span>
            <span style={{ color: '#212126' }}>Contract File</span>
          </span>
        </div>

        {/* Second Row: Tabs and Upload Button */}
        <div className="flex items-center justify-between mt-6">
          <div
            className="flex items-center"
            style={{
              width: '100%',
              maxWidth: '550px',
              height: '38px',
              borderRadius: '8px',
              border: '1px solid #E9E9EA',
              backgroundColor: '#FFFFFF',
              padding: '4px',
              gap: '0px'
            }}
          >
            {tabs.map((tab) => {
              const isActive = activeTab === tab.key

              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabClick(tab.key)}
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    color: isActive ? '#FFFFFF' : '#212126',
                    backgroundColor: isActive ? '#212126' : 'transparent',
                    borderRadius: '6px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    justifyContent: 'center',
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>

          {/* Upload Contract File Button - Only show on Buy and Sale tabs */}
          {(activeTab === 'buy-contract' || activeTab === 'sale-contract') && (
            <button
              onClick={() => setIsUploadModalOpen(true)}
              className="flex items-center justify-center gap-2 text-white font-medium rounded-md hover:opacity-90 transition-opacity"
              style={{
                height: '38px',
                backgroundColor: '#212126',
                borderRadius: '6px',
                padding: '10px 16px',
                gap: '8px',
                flexShrink: 0,
                marginLeft: '16px'
              }}
            >
              <FiPlus className="w-4 h-4" />
              <span>Upload Contract File</span>
            </button>
          )}
        </div>
      </div>

      {/* Table Container with Search and Filters */}
      <div 
        className="bg-white"
        style={{
          width: '100%',
          maxWidth: '1514px',
          minHeight: '600px',
          border: '1px solid #E9E9EA',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}
      >
        {/* Hidden file input for row-level upload */}
        <input
          ref={rowUploadInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,image/*"
          className="hidden"
          onChange={handleRowUploadFileChange}
        />
        {/* Error messages */}
        {(actionError || contractsError) && (
          <div
            className="mx-4 mt-3 rounded-lg border flex items-start gap-3 px-4 py-3"
            style={{
              fontFamily: 'Lexend Deca',
              backgroundColor: '#FEE2E2',
              borderColor: '#FECACA',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E93323" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <div style={{ flex: 1, fontSize: '13px', lineHeight: '20px', color: '#991B1B' }}>
              {actionError || contractsError}
            </div>
            <button
              onClick={() => setActionError(null)}
              style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: '#991B1B', opacity: 0.5, padding: '2px' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}
        {/* Search and Filter Section */}
        <div className="p-4" style={{ flexShrink: 0 }}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            {/* Search Bar */}
            <div className="relative flex-1 w-full sm:max-w-md">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={getSearchPlaceholder(activeTab)}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  lineHeight: '20px',
                  height: '40px'
                }}
              />
            </div>

            {/* Customer/Supplier Filters */}
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
              {activeTab === 'buy-contract' ? (
                <>
                  {/* Select Supplier Dropdown for Buy Contract */}
                  <div className="relative">
                    <select
                      value={supplierFilter}
                      onChange={(e) => setSupplierFilter(e.target.value)}
                      className="px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none text-sm"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        lineHeight: '20px',
                        color: '#050521',
                        height: '40px',
                        minWidth: '140px'
                      }}
                    >
                      <option value="Select Supplier">Select Supplier</option>
                      {suppliersForFilter.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    <FiChevronDown
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none w-4 h-4"
                      style={{
                        opacity: 0.8,
                        color: '#050521'
                      }}
                    />
                  </div>

                  {/* Add Supplier Button */}
                  <button
                    onClick={() => setIsAddSupplierModalOpen(true)}
                    className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity text-sm"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      lineHeight: '20px',
                      height: '40px',
                      whiteSpace: 'nowrap',
                      backgroundColor: '#212126'
                    }}
                  >
                    Add Supplier
                  </button>
                </>
              ) : (
                <>
                  {/* Select Customer Dropdown for Sale Contract */}
                  <div className="relative">
                    <select
                      value={supplierFilter}
                      onChange={(e) => setSupplierFilter(e.target.value)}
                      className="px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none text-sm"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        lineHeight: '20px',
                        color: '#050521',
                        height: '40px',
                        minWidth: '140px'
                      }}
                    >
                      <option value="Select Supplier">Select Customer</option>
                      {suppliersForFilter.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    <FiChevronDown
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none w-4 h-4"
                      style={{
                        opacity: 0.8,
                        color: '#050521'
                      }}
                    />
                  </div>

                </>
              )}
            </div>
          </div>
        </div>

        {/* Inner Table Container */}
        <div 
          className="bg-white"
          style={{
            flex: 1,
            border: '1px solid #E9E9EA',
            margin: '0 16px 16px 16px',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}
        >
          <div 
            style={{ 
              flex: 1, 
              minWidth: 0, 
              overflowX: 'auto', 
              overflowY: 'hidden', 
              position: 'relative'
            }}
          >
          <table style={{ borderCollapse: 'collapse', tableLayout: 'auto', width: '100%', minWidth: 'max-content' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #E9E9EA', borderTop: '1px solid #E9E9EA' }}>
                {activeTab === 'buy-contract' ? (
                  <>
                    {/* Buy Contract: Contract Number, Contract Type, Supplier, Factory, Product, SKU, Qty, Price, Incoterm, POL, POD, Delivery Conditions, Shipping Period, Payment Terms, Buy Contract, Date Modified, Action */}
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '8px', borderLeft: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Contract Number</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>Contract Type</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>Supplier</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>Factory</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>Product</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>SKU</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>Qty</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>Price</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>Incoterm</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>POL</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>POD</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>Delivery Conditions</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>Shipping Period</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>Payment Terms</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>Buy Contract</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>Date Modified</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '8px', paddingRight: '12px', borderRight: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Action</th>
                  </>
                ) : (
                  <>
                    {/* Sale Contract: Contract Number, Contract Type, Customer, Factory, Product, SKU, Qty, Price, Incoterm, POL, POD, Delivery Conditions, Shipping Period, Payment Terms, Sale Contract, Date Modified, Action */}
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', borderLeft: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Contract Number</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>Contract Type</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>Customer</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>Factory</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>Product</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>SKU</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>Qty</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>Price</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>Incoterm</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>POL</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>POD</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>Delivery Conditions</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>Shipping Period</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>Payment Terms</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>Sale Contract</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>Date Modified</th>
                    <th className="text-left py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '14px', paddingRight: '14px', borderRight: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Action</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {contractsLoading ? (
                <tr>
                  <td colSpan={17} className="py-8 text-center">
                    <p style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', color: '#69697A' }}>
                      Loading...
                    </p>
                  </td>
                </tr>
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan={17} className="py-8 text-center">
                    <p
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '14px',
                        color: '#69697A'
                      }}
                    >
                      No contracts found
                    </p>
                  </td>
                </tr>
              ) : (
                currentData.map((item, index) => {
                  const isExpanded = expandedFolders.has(item.id)
                  const isLastRow = index === currentData.length - 1
                  const isFolder = item.isFolder
                  const isChild = !!item.parentId

                  // Determine background color
                  let backgroundColor = 'transparent'
                  if (isFolder && isExpanded) {
                    backgroundColor = '#F2F4F2' // Folder row when expanded
                  } else if (isChild) {
                    backgroundColor = '#F8F9FB' // Child items
                  }

                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-gray-50 transition-colors"
                      style={{
                        borderBottom: isLastRow || isChild ? 'none' : '1px solid #E9E9EA',
                        backgroundColor: backgroundColor,
                        cursor: !isFolder && (activeTab === 'sale-contract' || activeTab === 'buy-contract') ? 'pointer' : 'default'
                      }}
                      onClick={() => !isFolder && (activeTab === 'sale-contract' || activeTab === 'buy-contract') && handleRowClick(item)}
                    >
                      {activeTab === 'buy-contract' ? (
                        <>
                          {/* Buy Contract columns: Contract Number, Contract Type, Supplier, Factory, Product, SKU, Qty, Price, Incoterm, POL, POD, Delivery Conditions, Shipping Period, Payment Terms, Buy Contract, Date Modified */}
                          <td 
                            className="py-2 text-xs sm:text-sm" 
                            style={{ 
                              fontFamily: 'Lexend Deca', 
                              fontWeight: 400, 
                              lineHeight: '22px', 
                              color: '#212126', 
                              paddingLeft: '12px', 
                              paddingRight: '8px', 
                              borderLeft: '1px solid #E9E9EA', 
                              whiteSpace: 'nowrap', 
                              position: 'relative'
                            }}
                          >
                            {isChild && !isLastRow && (
                              <div
                                style={{
                                  position: 'absolute',
                                  left: '40px',
                                  right: 0,
                                  bottom: 0,
                                  height: '1px',
                                  backgroundColor: '#E9E9EA'
                                }}
                              />
                            )}
                            <div className="flex items-center gap-2" style={{ paddingLeft: isChild ? '24px' : '0px' }}>
                              {isChild && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    left: '16px',
                                    top: 0,
                                    bottom: 0,
                                    width: '1px',
                                    backgroundColor: '#E9E9EA',
                                    opacity: 1
                                  }}
                                />
                              )}
                              {isFolder && (
                                <FiFolder className="w-4 h-4" style={{ color: '#212126', flexShrink: 0 }} />
                              )}
                              <span>{item.ref || item.name}</span>
                            </div>
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>
                            {item.contractType}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>
                            {item.supplier || 'No Supplier'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>
                            {item.factory || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>
                            {item.product || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>
                            {item.sku || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>
                            {item.qty}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>
                            {formatContractPrice(item.price === '-' ? null : item.price, item.priceCurrency)}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>
                            {item.incoterm || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>
                            {item.pol || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>
                            {item.pod || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>
                            {item.deliveryConditions || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>
                            {item.deliveryPeriod || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>
                            {item.paymentTerms || '-'}
                          </td>
                          <td className="py-2" style={{ paddingLeft: '8px', paddingRight: '8px' }} onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                disabled={uploadingContractId === item.id}
                                onClick={() => handleContractFileButtonClick(item, item.buyContract === 'Uploaded')}
                                title={item.buyContract === 'Uploaded' ? 'View uploaded file' : 'Upload file'}
                                className="px-3 py-1 rounded hover:opacity-90 transition-opacity text-xs sm:text-sm disabled:opacity-60"
                                style={{
                                  fontFamily: 'Lexend Deca',
                                  fontWeight: 400,
                                  lineHeight: '20px',
                                  color: item.buyContract === 'Uploaded' ? '#13AB5B' : '#212126',
                                  backgroundColor: item.buyContract === 'Uploaded' ? '#E8F7EF' : '#FFFFFF',
                                  border: item.buyContract === 'Uploaded' ? '1px solid #13AB5B' : '1px solid #E9E9EA',
                                  borderRadius: '6px',
                                  paddingTop: '6px',
                                  paddingRight: '10px',
                                  paddingBottom: '6px',
                                  paddingLeft: '10px',
                                  width: item.buyContract === 'Upload' ? '62px' : 'auto',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {uploadingContractId === item.id ? '...' : (item.buyContract || 'Upload')}
                              </button>
                              {item.buyContract === 'Uploaded' && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteContractFile(item.id)}
                                  className="hover:opacity-80 transition-opacity"
                                  title="Remove file"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: '1px solid #EF4444',
                                    backgroundColor: '#FEE2E2',
                                    flexShrink: 0
                                  }}
                                >
                                  <FiTrash2 style={{ width: '14px', height: '14px', color: '#EF4444' }} />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap' }}>
                            {formatDateToDDMMMYYYY(item.dateModified)}
                          </td>
                        </>
                      ) : (
                        <>
                          {/* Sale Contract columns: Contract Number, Contract Type, Customer, Factory, Product, SKU, Qty, Price, Incoterm, POL, POD, Delivery Conditions, Shipping Period, Payment Terms, Sale Contract, Date Modified */}
                          <td 
                            className="py-2 text-xs sm:text-sm" 
                            style={{ 
                              fontFamily: 'Lexend Deca', 
                              fontWeight: 400, 
                              lineHeight: '22px', 
                              color: '#212126', 
                              paddingLeft: '14px', 
                              paddingRight: '14px', 
                              borderLeft: '1px solid #E9E9EA', 
                              whiteSpace: 'nowrap', 
                              position: 'relative'
                            }}
                          >
                            {isChild && !isLastRow && (
                              <div
                                style={{
                                  position: 'absolute',
                                  left: '40px',
                                  right: 0,
                                  bottom: 0,
                                  height: '1px',
                                  backgroundColor: '#E9E9EA'
                                }}
                              />
                            )}
                            <div className="flex items-center gap-2" style={{ paddingLeft: isChild ? '24px' : '0px' }}>
                              {isChild && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    left: '16px',
                                    top: 0,
                                    bottom: 0,
                                    width: '1px',
                                    backgroundColor: '#E9E9EA',
                                    opacity: 1
                                  }}
                                />
                              )}
                              {isFolder && (
                                <FiFolder className="w-4 h-4" style={{ color: '#212126', flexShrink: 0 }} />
                              )}
                              <span>{item.ref || item.name}</span>
                            </div>
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>
                            {item.contractType}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>
                            {item.customer || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>
                            {item.factory || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>
                            {item.product || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>
                            {item.sku || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>
                            {item.qty}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>
                            {formatContractPrice(item.price === '-' ? null : item.price, item.priceCurrency)}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>
                            {item.incoterm || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>
                            {item.pol || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>
                            {item.pod || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>
                            {item.deliveryConditions || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>
                            {item.deliveryPeriod || '-'}
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>
                            {item.paymentTerms || '-'}
                          </td>
                          <td className="py-2" style={{ paddingLeft: '14px', paddingRight: '14px' }} onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                disabled={uploadingContractId === item.id}
                                onClick={() => handleContractFileButtonClick(item, item.saleContract === 'Uploaded')}
                                title={item.saleContract === 'Uploaded' ? 'View uploaded file' : 'Upload file'}
                                className="px-3 py-1 rounded hover:opacity-90 transition-opacity text-xs sm:text-sm disabled:opacity-60"
                                style={{
                                  fontFamily: 'Lexend Deca',
                                  fontWeight: 400,
                                  lineHeight: '20px',
                                  color: item.saleContract === 'Uploaded' ? '#13AB5B' : '#212126',
                                  backgroundColor: item.saleContract === 'Uploaded' ? '#E8F7EF' : '#FFFFFF',
                                  border: item.saleContract === 'Uploaded' ? '1px solid #13AB5B' : '1px solid #E9E9EA',
                                  borderRadius: '6px',
                                  paddingTop: '6px',
                                  paddingRight: '10px',
                                  paddingBottom: '6px',
                                  paddingLeft: '10px',
                                  width: item.saleContract === 'Upload' ? '62px' : 'auto',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {uploadingContractId === item.id ? '...' : (item.saleContract || 'Upload')}
                              </button>
                              {item.saleContract === 'Uploaded' && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteContractFile(item.id)}
                                  className="hover:opacity-80 transition-opacity"
                                  title="Remove file"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: '1px solid #EF4444',
                                    backgroundColor: '#FEE2E2',
                                    flexShrink: 0
                                  }}
                                >
                                  <FiTrash2 style={{ width: '14px', height: '14px', color: '#EF4444' }} />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="py-2 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', paddingLeft: '14px', paddingRight: '14px', whiteSpace: 'nowrap' }}>
                            {formatDateToDDMMMYYYY(item.dateModified)}
                          </td>
                        </>
                      )}
                      <td className="py-2" style={{ paddingLeft: '8px', paddingRight: '12px', borderRight: '1px solid #E9E9EA', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                        {isFolder ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleFolder(item.id)
                            }}
                            className="p-2 hover:opacity-80 rounded transition-colors"
                            title={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? (
                              <FiChevronUp className="w-4 h-4" style={{ color: '#212126' }} />
                            ) : (
                              <FiChevronDown className="w-4 h-4" style={{ color: '#212126' }} />
                            )}
                          </button>
                        ) : (
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleDropdown(item.id)
                              }}
                              className="p-2 hover:opacity-80 rounded transition-colors"
                              title="More options"
                              style={isChild ? {
                                width: '24px',
                                height: '24px',
                                backgroundColor: '#EAF1FF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0
                              } : {}}
                            >
                              <FiMoreVertical 
                                className={isChild ? "" : "w-4 h-4"} 
                                style={isChild ? { 
                                  width: '14px', 
                                  height: '14px', 
                                  color: '#212126' 
                                } : { color: '#212126' }} 
                              />
                            </button>
                            {openDropdownId === item.id && (
                              <div
                                style={{
                                  position: 'absolute',
                                  right: 0,
                                  top: '100%',
                                  marginTop: '4px',
                                  backgroundColor: 'white',
                                  border: '1px solid #E9E9EA',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                  zIndex: 1000,
                                  minWidth: '120px',
                                  padding: '4px'
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setEditContractId(item.id)
                                    setIsEditModalOpen(true)
                                    setOpenDropdownId(null)
                                  }}
                                  style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 12px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontFamily: 'Lexend Deca',
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    color: '#212126'
                                  }}
                                  className="hover:bg-gray-50 rounded transition-colors"
                                >
                                  <div style={{ width: '24px', height: '24px', backgroundColor: '#FEF9EB', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <FiEdit style={{ width: '14px', height: '14px', color: '#F59E0B' }} />
                                  </div>
                                  <span>Edit</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (activeTab === 'sale-contract' || activeTab === 'buy-contract') {
                                      handleViewContract(item)
                                    }
                                  }}
                                  style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 12px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: (activeTab === 'sale-contract' || activeTab === 'buy-contract') ? 'pointer' : 'default',
                                    fontFamily: 'Lexend Deca',
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    color: (activeTab === 'sale-contract' || activeTab === 'buy-contract') ? '#212126' : '#69697A',
                                    opacity: (activeTab === 'sale-contract' || activeTab === 'buy-contract') ? 1 : 0.5
                                  }}
                                  className={(activeTab === 'sale-contract' || activeTab === 'buy-contract') ? "hover:bg-gray-50 rounded transition-colors" : ""}
                                  disabled={activeTab !== 'sale-contract' && activeTab !== 'buy-contract'}
                                >
                                  <div style={{ width: '24px', height: '24px', backgroundColor: '#EAF1FF', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <FiEye style={{ width: '14px', height: '14px', color: '#2C71F6' }} />
                                  </div>
                                  <span>View</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDownloadContract(item)
                                  }}
                                  style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 12px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontFamily: 'Lexend Deca',
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    color: '#212126'
                                  }}
                                  className="hover:bg-gray-50 rounded transition-colors"
                                >
                                  <div style={{ width: '24px', height: '24px', backgroundColor: '#E8F7EF', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <FiDownload style={{ width: '14px', height: '14px', color: '#10B981' }} />
                                  </div>
                                  <span>Download</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDeleteContract(item)
                                  }}
                                  style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 12px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontFamily: 'Lexend Deca',
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    color: '#212126'
                                  }}
                                  className="hover:bg-gray-50 rounded transition-colors"
                                >
                                  <div style={{ width: '24px', height: '24px', backgroundColor: '#FEE2E2', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <FiTrash2 style={{ width: '14px', height: '14px', color: '#EF4444' }} />
                                  </div>
                                  <span>Delete</span>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '1px solid #E9E9EA' }}>
                <td colSpan={17} className="py-3 px-2 sm:px-4" style={{ paddingLeft: '16px', paddingRight: '16px' }}>
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                    <span
                      className="text-xs sm:text-sm"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        lineHeight: '22px',
                        color: '#69697A'
                      }}
                    >
                      Showing {totalResults === 0 ? '0' : `${startIndex}-${endIndex}`} of {totalResults} Results
                    </span>
                    <div className="flex items-center gap-2">
                      <PageSizeSelect
                        value={resultsPerPage}
                        onChange={(size) => {
                          setResultsPerPage(size)
                          setCurrentPage(1)
                        }}
                      />
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 rounded hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          lineHeight: '22px',
                          color: '#212126',
                          border: '1px solid #E9E9EA',
                          backgroundColor: 'transparent'
                        }}
                      >
                        <FiChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Previous</span>
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className="px-2 sm:px-3 py-1 rounded hover:opacity-90 transition-opacity text-xs sm:text-sm"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 400,
                            lineHeight: '22px',
                            color: currentPage === page ? '#FFFFFF' : '#212126',
                            backgroundColor: currentPage === page ? '#212126' : 'transparent',
                            border: '1px solid #E9E9EA'
                          }}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 rounded hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          lineHeight: '22px',
                          color: '#212126',
                          border: '1px solid #E9E9EA',
                          backgroundColor: 'transparent'
                        }}
                      >
                        <span className="hidden sm:inline">Next</span>
                        <FiChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
          </div>
        </div>
      </div>

      {/* Image popup modal (Buy Contract – when file is an image) */}
      <ImagePopupModal
        isOpen={!!imagePopupSrc}
        imageSrc={imagePopupSrc || ''}
        alt="Contract file"
        onClose={() => setImagePopupSrc(null)}
      />

      {/* Contract file viewer dialog (iframe for PDF/non-image) */}
      {fileViewerUrl && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            boxSizing: 'border-box'
          }}
          onClick={() => setFileViewerUrl(null)}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              width: '100%',
              maxWidth: '900px',
              height: '85vh',
              maxHeight: '800px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderBottom: '1px solid #E9E9EA',
                flexShrink: 0
              }}
            >
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '16px', color: '#212126' }}>
                Contract file
              </span>
              <button
                type="button"
                onClick={() => setFileViewerUrl(null)}
                style={{
                  padding: '8px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                className="hover:opacity-80"
                title="Close"
              >
                <FiX style={{ width: '20px', height: '20px', color: '#212126' }} />
              </button>
            </div>
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                cursor: fileViewerDrag ? 'grabbing' : 'grab',
                userSelect: 'none'
              }}
              onMouseDown={(e) => {
                if (e.button === 0) {
                  setFileViewerDrag({
                    startX: e.clientX,
                    startY: e.clientY,
                    startPanX: fileViewerPan.x,
                    startPanY: fileViewerPan.y
                  })
                }
              }}
            >
              <div
                style={{
                  transform: `translate(${fileViewerPan.x}px, ${fileViewerPan.y}px)`,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none'
                }}
              >
                <iframe
                  title="Contract file"
                  src={fileViewerUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '400px',
                    border: 'none',
                    pointerEvents: 'none'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Contract File Modal */}
      <UploadContractFileModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        initialContractType={activeTab === 'buy-contract' ? 'Buy Contract' : 'Sale Contract'}
        onSuccess={refetch}
      />

      {/* Add Supplier Modal */}
      <AddSupplierModal
        isOpen={isAddSupplierModalOpen}
        onClose={() => setIsAddSupplierModalOpen(false)}
      />

      {/* Edit Contract Modal */}
      <EditContractModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false)
          setEditContractId(null)
        }}
        contractId={editContractId}
        onSuccess={refetch}
      />

      {/* Contract Details Modal */}
      <ContractDetailsModal
        isOpen={isContractDetailsModalOpen}
        onClose={() => {
          setIsContractDetailsModalOpen(false)
          setSelectedContract(null)
        }}
        onViewFile={
          selectedContract?.fileUrl
            ? () => { void handleDownloadContract(selectedContract) }
            : undefined
        }
        contractData={selectedContract ? {
          id: selectedContract.id,
          name: selectedContract.name,
          ref: selectedContract.ref,
          type: selectedContract.type,
          supplier: selectedContract.supplier,
          contractType: selectedContract.contractType,
          product: selectedContract.product,
          sku: selectedContract.sku,
          qty: selectedContract.qty,
          price: selectedContract.price,
          priceCurrency: selectedContract.priceCurrency,
          terms: selectedContract.terms || selectedContract.deliveryConditions,
          deliveryConditions: selectedContract.deliveryConditions,
          deliveryPeriod: selectedContract.deliveryPeriod,
          buyContract: selectedContract.buyContract,
          saleContract: selectedContract.saleContract,
          dateModified: selectedContract.dateModified,
          customer: selectedContract.customer,
          fileUrl: selectedContract.fileUrl,
          factory: selectedContract.factory,
          incoterm: selectedContract.incoterm,
          pol: selectedContract.pol,
          pod: selectedContract.pod,
          paymentTerms: selectedContract.paymentTerms,
        } : undefined}
      />
    </div>
  )
}

export default ContractFile
